import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuthStore } from '../stores/authStore';
import { kakaoLogout, getStoredAccessToken } from '../services/kakaoAuth';
import LoginModal from './auth/LoginModal';
import EditProfileModal from './auth/EditProfileModal';

const KAKAO_CHAT_URL = 'http://pf.kakao.com/_UFxkBl/chat';

const Header: React.FC = () => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const { user, isAuthenticated, logout, updateUser } = useAuthStore();
  const [isReservationDropdownOpen, setIsReservationDropdownOpen] = useState(false);
  const [isUserDropdownOpen, setIsUserDropdownOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isLoginModalOpen, setIsLoginModalOpen] = useState(false);
  const [isEditProfileModalOpen, setIsEditProfileModalOpen] = useState(false);

  // 기존 사용자의 user.id가 카카오 ID인 경우 자동으로 수정
  useEffect(() => {
    const fixUserIdIfNeeded = async () => {
      if (isAuthenticated && user?.kakaoId && user.id === user.kakaoId) {
        // user.id가 카카오 ID와 같으면 백엔드에서 실제 user ID 조회
        try {
          const response = await fetch(`${process.env.REACT_APP_API_URL}/users/kakao/${user.kakaoId}`);
          if (response.ok) {
            const backendUser = await response.json();
            // 실제 DB user ID로 업데이트
            updateUser({ id: backendUser.id });
            console.log(`Fixed user ID from ${user.id} to ${backendUser.id}`);
          }
        } catch (error) {
          console.error('Failed to fix user ID:', error);
        }
      }
    };
    fixUserIdIfNeeded();
  }, [isAuthenticated, user, updateUser]);

  const handleLogout = async () => {
    const accessToken = getStoredAccessToken();
    if (accessToken) {
      await kakaoLogout(accessToken);
    }
    logout();
    setIsUserDropdownOpen(false);
    navigate('/');
  };

  const changeLanguage = (language: 'ko' | 'en') => {
    i18n.changeLanguage(language);
  };

  const languageToggle = (variant: 'desktop' | 'mobile') => {
    const isDesktop = variant === 'desktop';

    return (
      <div
        className={`inline-flex items-center rounded-lg border ${
          isDesktop
            ? 'border-white/30 bg-white/15 p-0.5'
            : 'border-gray-200 bg-gray-100 p-1'
        }`}
        role="group"
        aria-label={t('language.label')}
      >
        {(['ko', 'en'] as const).map((language) => {
          const isActive = i18n.language === language;
          return (
            <button
              key={language}
              type="button"
              onClick={() => changeLanguage(language)}
              className={`min-w-10 rounded-md px-2.5 py-1.5 text-xs font-bold transition-colors ${
                isDesktop
                  ? isActive
                    ? 'bg-white text-blue-600 shadow-sm'
                    : 'text-white/85 hover:bg-white/15 hover:text-white'
                  : isActive
                  ? 'bg-blue-600 text-white shadow-sm'
                  : 'text-gray-600 hover:bg-white hover:text-gray-900'
              }`}
              aria-pressed={isActive}
            >
              {t(`language.${language}`)}
            </button>
          );
        })}
      </div>
    );
  };

  return (
    <header className="bg-gradient-to-r from-blue-600 to-cyan-500 shadow-md">
      <div className="max-w-7xl mx-auto px-4">
        {/* Desktop Header */}
        <div className="hidden md:flex items-center justify-between h-16">
          {/* Logo */}
          <div
            onClick={() => navigate('/')}
            className="cursor-pointer flex items-center gap-3"
          >
            <img src="/logo.svg" alt="굿타임 렌터카" className="h-10" />
          </div>

          {/* Navigation Menu */}
          <nav className="flex items-center gap-1">
            {/* 차량예약 Dropdown */}
            <div
              className="relative"
              onMouseEnter={() => setIsReservationDropdownOpen(true)}
              onMouseLeave={() => setIsReservationDropdownOpen(false)}
            >
              <button
                className="text-white hover:bg-white/20 px-4 py-2 rounded-lg transition-colors font-medium flex items-center gap-1"
              >
                {t('nav.vehicleReservation')}
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>

              {/* Dropdown Menu */}
              {isReservationDropdownOpen && (
                <div className="absolute top-full left-0 mt-0 w-40 bg-white rounded-lg shadow-lg py-2 z-50">
                  <button
                    onClick={() => {
                      navigate('/');
                      setIsReservationDropdownOpen(false);
                    }}
                    className="w-full text-left px-4 py-2 hover:bg-gray-100 text-gray-700 transition-colors"
                  >
                    {t('nav.realTimeReservation')}
                  </button>
                  <button
                    onClick={() => {
                      navigate('/reservation');
                      setIsReservationDropdownOpen(false);
                    }}
                    className="w-full text-left px-4 py-2 hover:bg-gray-100 text-gray-700 transition-colors"
                  >
                    {t('nav.reservationCheck')}
                  </button>
                </div>
              )}
            </div>

            {/* 의전&투어 */}
            <button
              onClick={() => navigate('/vip')}
              className="text-white hover:bg-white/20 px-4 py-2 rounded-lg transition-colors font-medium"
            >
              {t('nav.vipService')}
            </button>

            {/* 차량안내 */}
            <button
              onClick={() => navigate('/rental-guide')}
              className="text-white hover:bg-white/20 px-4 py-2 rounded-lg transition-colors font-medium"
            >
              {t('nav.vehicleGuide')}
            </button>

            {/* 이용안내 */}
            <button
              onClick={() => navigate('/service-guide')}
              className="text-white hover:bg-white/20 px-4 py-2 rounded-lg transition-colors font-medium"
            >
              {t('nav.serviceGuide')}
            </button>

            {/* 예약확인 */}
            <button
              onClick={() => navigate('/reservation')}
              className="text-white hover:bg-white/20 px-4 py-2 rounded-lg transition-colors font-medium"
            >
              {t('nav.reservationCheck')}
            </button>
          </nav>

          {/* Right Side Buttons */}
          <div className="flex items-center gap-2">
            {languageToggle('desktop')}
            <a
              href={KAKAO_CHAT_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="bg-[#FEE500] hover:bg-[#f4dc00] text-[#191919] px-4 py-2 rounded-lg transition-colors font-semibold text-sm inline-flex items-center gap-2"
            >
              <img src="/assets/images/kakao.svg" alt="" className="h-6 w-6" aria-hidden="true" />
              {t('nav.kakaoChat')}
            </a>

            {isAuthenticated && user ? (
              /* Logged In User */
              <div
                className="relative"
                onMouseEnter={() => setIsUserDropdownOpen(true)}
                onMouseLeave={() => setIsUserDropdownOpen(false)}
              >
                <button
                  className="flex items-center gap-2 bg-white/20 hover:bg-white/30 text-white px-4 py-2 rounded-lg transition-colors font-medium text-sm backdrop-blur-sm"
                >
                  {user.profileImage ? (
                    <img src={user.profileImage} alt={user.nickname || t('nav.user')} className="w-6 h-6 rounded-full" />
                  ) : (
                    <div className="w-6 h-6 rounded-full bg-white/30 flex items-center justify-center">
                      <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd" />
                      </svg>
                    </div>
                  )}
                  <span>{user.nickname || t('nav.user')}</span>
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>

                {/* User Dropdown Menu */}
                {isUserDropdownOpen && (
                  <div className="absolute top-full right-0 mt-0 w-48 bg-white rounded-lg shadow-lg py-2 z-50">
                    <div className="px-4 py-2 border-b border-gray-200">
                      <p className="text-sm font-medium text-gray-900">{user.nickname || t('nav.user')}</p>
                      {user.email && <p className="text-xs text-gray-500">{user.email}</p>}
                    </div>
                    <button
                      onClick={() => {
                        navigate('/my-reservations');
                        setIsUserDropdownOpen(false);
                      }}
                      className="w-full text-left px-4 py-2 hover:bg-gray-100 text-gray-700 transition-colors text-sm"
                    >
                      {t('nav.myReservations')}
                    </button>
                    <button
                      onClick={() => {
                        navigate('/my-coupons');
                        setIsUserDropdownOpen(false);
                      }}
                      className="w-full text-left px-4 py-2 hover:bg-gray-100 text-gray-700 transition-colors text-sm"
                    >
                      {t('nav.myCoupons')}
                    </button>
                    <button
                      onClick={() => {
                        setIsEditProfileModalOpen(true);
                        setIsUserDropdownOpen(false);
                      }}
                      className="w-full text-left px-4 py-2 hover:bg-gray-100 text-gray-700 transition-colors text-sm"
                    >
                      {t('nav.editProfile')}
                    </button>
                    <button
                      onClick={handleLogout}
                      className="w-full text-left px-4 py-2 hover:bg-gray-100 text-red-600 transition-colors text-sm"
                    >
                      {t('nav.logout')}
                    </button>
                  </div>
                )}
              </div>
            ) : (
              /* Not Logged In */
              <>
                <button
                  onClick={() => setIsLoginModalOpen(true)}
                  className="bg-white text-blue-700 hover:bg-blue-50 px-4 py-2 rounded-lg transition-colors font-bold text-sm shadow-sm ring-1 ring-white/70"
                >
                  {t('nav.login')}
                </button>
                <button
                  onClick={() => setIsLoginModalOpen(true)}
                  className="bg-slate-950 hover:bg-slate-800 text-white px-4 py-2 rounded-lg transition-colors font-bold text-sm shadow-md ring-1 ring-white/20"
                >
                  {t('nav.signup')}
                </button>
              </>
            )}
          </div>
        </div>

        {/* Mobile Header */}
        <div className="md:hidden flex items-center justify-between h-16">
          {/* Logo */}
          <div
            onClick={() => navigate('/')}
            className="cursor-pointer"
          >
            <img src="/logo.svg" alt="굿타임 렌터카" className="h-8" />
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="text-white p-2"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              {isMobileMenuOpen ? (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              ) : (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              )}
            </svg>
          </button>
        </div>

        {/* Mobile Menu Overlay */}
        {isMobileMenuOpen && (
          <>
            {/* Backdrop */}
            <div
              className="fixed inset-0 bg-black/50 z-40 md:hidden"
              onClick={() => setIsMobileMenuOpen(false)}
            />

            {/* Slide-in Menu */}
            <div className="fixed top-0 right-0 bottom-0 w-4/5 max-w-sm bg-white z-50 md:hidden shadow-2xl overflow-y-auto animate-slide-in">
              {/* Header with Logo */}
              <div className="flex items-center justify-between p-4 border-b border-gray-200">
                <img src="/logo.svg" alt="굿타임 렌터카" className="h-7" />
                <button
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="text-gray-600 p-2 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              {/* Menu Items */}
              <div className="py-2">
            <div className="px-4 py-3">
              {languageToggle('mobile')}
            </div>
            <button
              onClick={() => {
                navigate('/');
                setIsMobileMenuOpen(false);
              }}
              className="w-full text-left px-4 py-3 hover:bg-gray-100 text-gray-700"
            >
              {t('nav.realTimeReservation')}
            </button>
            <button
              onClick={() => {
                navigate('/reservation');
                setIsMobileMenuOpen(false);
              }}
              className="w-full text-left px-4 py-3 hover:bg-gray-100 text-gray-700"
            >
              {t('nav.reservationCheck')}
            </button>
            <button
              onClick={() => {
                navigate('/vip');
                setIsMobileMenuOpen(false);
              }}
              className="w-full text-left px-4 py-3 hover:bg-gray-100 text-gray-700"
            >
              {t('nav.vipService')}
            </button>
            <button
              onClick={() => {
                navigate('/rental-guide');
                setIsMobileMenuOpen(false);
              }}
              className="w-full text-left px-4 py-3 hover:bg-gray-100 text-gray-700"
            >
              {t('nav.vehicleGuide')}
            </button>
            <button
              onClick={() => {
                navigate('/service-guide');
                setIsMobileMenuOpen(false);
              }}
              className="w-full text-left px-4 py-3 hover:bg-gray-100 text-gray-700"
            >
              {t('nav.serviceGuide')}
            </button>
            <a
              href={KAKAO_CHAT_URL}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => setIsMobileMenuOpen(false)}
              className="flex w-full items-center gap-2 px-4 py-3 bg-[#FEE500] hover:bg-[#f4dc00] text-[#191919] font-semibold"
            >
              <img src="/assets/images/kakao.svg" alt="" className="h-6 w-6" aria-hidden="true" />
              {t('nav.kakaoChat')}
            </a>
            <div className="border-t border-gray-200 my-2"></div>

            {isAuthenticated && user ? (
              /* Logged In User - Mobile */
              <>
                <div className="px-4 py-3 bg-gray-50">
                  <div className="flex items-center gap-3">
                    {user.profileImage ? (
                      <img src={user.profileImage} alt={user.nickname || t('nav.user')} className="w-10 h-10 rounded-full" />
                    ) : (
                      <div className="w-10 h-10 rounded-full bg-gray-300 flex items-center justify-center">
                        <svg className="w-6 h-6 text-gray-600" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd" />
                        </svg>
                      </div>
                    )}
                    <div>
                      <p className="font-medium text-gray-900">{user.nickname || t('nav.user')}</p>
                      {user.email && <p className="text-xs text-gray-500">{user.email}</p>}
                    </div>
                  </div>
                </div>
                <button
                  onClick={() => {
                    navigate('/my-reservations');
                    setIsMobileMenuOpen(false);
                  }}
                  className="w-full text-left px-4 py-3 hover:bg-gray-100 text-gray-700"
                >
                  {t('nav.myReservations')}
                </button>
                <button
                  onClick={() => {
                    setIsEditProfileModalOpen(true);
                    setIsMobileMenuOpen(false);
                  }}
                  className="w-full text-left px-4 py-3 hover:bg-gray-100 text-gray-700"
                >
                  {t('nav.editProfile')}
                </button>
                <button
                  onClick={() => {
                    handleLogout();
                    setIsMobileMenuOpen(false);
                  }}
                  className="w-full text-left px-4 py-3 hover:bg-gray-100 text-red-600 font-medium"
                >
                  {t('nav.logout')}
                </button>
              </>
            ) : (
              /* Not Logged In - Mobile */
              <>
                <button
                  onClick={() => {
                    setIsLoginModalOpen(true);
                    setIsMobileMenuOpen(false);
                  }}
                  className="w-full text-left px-4 py-3 hover:bg-gray-100 text-blue-600 font-medium"
                >
                  {t('nav.login')}
                </button>
                <button
                  onClick={() => {
                    setIsLoginModalOpen(true);
                    setIsMobileMenuOpen(false);
                  }}
                  className="w-full text-left px-4 py-3 hover:bg-gray-100 text-blue-600 font-medium"
                >
                  {t('nav.signup')}
                </button>
              </>
            )}
              </div>
            </div>
          </>
        )}
      </div>

      {/* Login Modal */}
      <LoginModal isOpen={isLoginModalOpen} onClose={() => setIsLoginModalOpen(false)} />

      {/* Edit Profile Modal */}
      <EditProfileModal isOpen={isEditProfileModalOpen} onClose={() => setIsEditProfileModalOpen(false)} />
    </header>
  );
};

export default Header;
